// yarn add @nivo/core @nivo/geo
// https://raw.githubusercontent.com/plouc/nivo/master/website/src/data/components/geo/world_countries.json

import { AgChartOptions } from "@ag-grid-community/core";
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  HStack,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { ResponsiveChoropleth } from "@nivo/geo";
import { AgChartsReact } from "ag-charts-react";
import React from "react";
import { saveSvgAsPng } from "save-svg-as-png";
import { ColumnKeyEnum } from "../../constants";
import { getCasesCountOptions } from "../../routes/registries/util/casesCountChart";

import {
  getCasesMapOptions,
  ICasesMapInputs,
} from "../../routes/registries/util/casesMapChart";
import { cloneCanvas, downloadCanvas } from "../../utils/canvas";
import { LibBoxDNASpinner } from "../lib";
import { MaterialIcon } from "../shared/MaterialIcon";
import { Status } from "../shared/Status";

interface IProps {
  gridRef: any;
}
const initialCaseOptions = {
  data: [],
  domain: [],
  features: [],
};

export const CasesMapChart = ({ gridRef }: IProps) => {
  const [isOutOfSync, setIsOutOfSync] = React.useState(false);
  const [casesMapOptions, setCasesMapOptions] =
    React.useState<ICasesMapInputs>(initialCaseOptions);
  const [isLoadingDisplay, setIsLoadingDisplay] = React.useState(false);
  const [isErrorDisplay, setIsErrorDisplay] = React.useState(false);
  const [isAnyFilterPresent, setIsAnyFilterPresent] = React.useState(false);
  const [noRowsDisplay, setNoRowsDisplay] = React.useState(false);
  const [casesCountOptions, setCasesCountOptions] =
    React.useState<AgChartOptions>({});

  const toast = useToast();

  const handleOnRemoveFilters = () => {
    const gridApi = gridRef?.current?.api;

    if (gridApi) {
      gridApi.setFilterModel(null);
      toast({
        title: "Removed filters",
        description: ``,
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    }
    handleOnGenerate();
  };

  const handleOnDownloadCanvas = (chartId, filename) => {
    if (!chartId) {
      return;
    }

    // Anchor to the chart ID element
    const chartidElem = document.querySelector(`#${chartId}`);

    if (chartidElem) {
      // Find the canvas html elem under the chart
      const svgListElem = chartidElem.getElementsByTagName("svg");
      if (svgListElem && svgListElem[0]) {
        saveSvgAsPng(svgListElem[0], filename);
      }

      const canvasListElem = chartidElem.getElementsByTagName("canvas");

      if (canvasListElem && canvasListElem[0]) {
        // Make it bigger
        const canvasPrime = cloneCanvas(canvasListElem[0], 4);

        // Download
        if (canvasPrime) {
          downloadCanvas(canvasPrime, filename);
        }
      }
    }
  };

  const handleOnGenerate = React.useCallback(async () => {
    setIsOutOfSync(false);
    setIsLoadingDisplay(true);
    setIsErrorDisplay(false);
    setNoRowsDisplay(false);
    setIsAnyFilterPresent(false);
    setIsAnyFilterPresent(false);

    const gridApi = gridRef?.current?.api;

    if (gridApi) {
      const rowCount = gridApi.getDisplayedRowCount();
      const isAnyFilterPresentUpdate = gridApi.isAnyFilterPresent();

      setIsAnyFilterPresent(isAnyFilterPresentUpdate);

      if (rowCount) {
        // Make map chart
        const resCaseOptionsUpdate = getCasesMapOptions(
          gridRef,
          ColumnKeyEnum.country
        );

        if (resCaseOptionsUpdate) {
          setCasesMapOptions(resCaseOptionsUpdate);
        } else {
          setIsErrorDisplay(true);
        }

        // Make case chart
        const resCaseCountOptionsUpdate = getCasesCountOptions(
          gridRef,
          ColumnKeyEnum.created_date
        );

        if (resCaseCountOptionsUpdate) {
          setCasesCountOptions(resCaseCountOptionsUpdate);
        } else {
          setIsErrorDisplay(true);
        }
      } else {
        setNoRowsDisplay(true);
      }
    }

    setIsLoadingDisplay(false);
  }, [gridRef]);

  const onFilterChanged = React.useCallback(() => {
    setIsOutOfSync(true);
  }, []);

  React.useEffect(() => {
    handleOnGenerate();
  }, [handleOnGenerate]);

  React.useEffect(() => {
    const gridApi = gridRef?.current?.api;

    if (gridApi) {
      gridApi.addEventListener("filterChanged", onFilterChanged);
    }

    return () => {
      if (gridApi) {
        gridApi.removeEventListener("filterChanged", onFilterChanged);
      }
    };
  }, [onFilterChanged, gridRef]);

  return (
    <Stack h="full" px={6} pt={2} pb={8} flex={1} position="relative">
      {isErrorDisplay && (
        <Center h="full">
          <Status.IsError title="Failed to load data" />
        </Center>
      )}

      {noRowsDisplay && (
        <Center h="full">
          <Stack
            spacing={6}
            px={12}
            py={8}
            border="1px"
            borderColor={"border"}
            rounded="md"
            shadow={"sm"}
          >
            <Text>No rows available to display graph</Text>
            {isAnyFilterPresent && (
              <Button onClick={handleOnRemoveFilters}>Remove filters</Button>
            )}
          </Stack>
        </Center>
      )}

      {isLoadingDisplay && !noRowsDisplay && (
        <Center h="full">
          <LibBoxDNASpinner />
        </Center>
      )}

      {!isLoadingDisplay && !isErrorDisplay && !noRowsDisplay && (
        <HStack h="full" spacing={8}>
          <Box p={4} flex={1} h="full" position={"relative"}>
            <Box pos={"absolute"} top={4} right={4} zIndex={1}>
              <ButtonGroup w="full" size="sm" variant={"outline"}>
                <Button
                  onClick={() =>
                    handleOnDownloadCanvas(
                      "caseCountChart",
                      "geneial-cases-count.png"
                    )
                  }
                  rightIcon={
                    <MaterialIcon
                      name="file_download"
                      variant="outline"
                      fontSize="1rem"
                    />
                  }
                >
                  Download
                </Button>
                <Button
                  aria-label="Generate Cases Charts"
                  onClick={handleOnGenerate}
                  // isLoading={isLoadingVariantMap}
                  loadingText="Generating..."
                  // isDisabled={isLoadingVariantMap}
                  colorScheme={isOutOfSync ? "yellow" : "gray"}
                  variant={isOutOfSync ? "solid" : "outline"}
                  rightIcon={
                    <MaterialIcon
                      name="refresh"
                      variant="outline"
                      fontSize="1rem"
                    />
                  }
                >
                  Refresh
                </Button>
              </ButtonGroup>
            </Box>
            <Box id="caseCountChart" h="full">
              <AgChartsReact options={casesCountOptions} />
            </Box>
          </Box>

          <Box p={4} flex={1} h="full" id="mapChart" position={"relative"}>
            <Text textAlign={"center"} textStyle="header" fontSize="xl">
              New Cases
            </Text>
            <Box pos={"absolute"} top={4} right={4} zIndex={1}>
              <ButtonGroup w="full" size="sm" variant={"outline"}>
                <Button
                  onClick={() =>
                    handleOnDownloadCanvas("mapChart", "geneial-cases-map.png")
                  }
                  rightIcon={
                    <MaterialIcon
                      name="file_download"
                      variant="outline"
                      fontSize="1rem"
                    />
                  }
                >
                  Download
                </Button>
                <Button
                  aria-label="Generate Cases Charts"
                  onClick={handleOnGenerate}
                  // isLoading={isLoadingVariantMap}
                  loadingText="Generating..."
                  // isDisabled={isLoadingVariantMap}
                  colorScheme={isOutOfSync ? "yellow" : "gray"}
                  variant={isOutOfSync ? "solid" : "outline"}
                  rightIcon={
                    <MaterialIcon
                      name="refresh"
                      variant="outline"
                      fontSize="1rem"
                    />
                  }
                >
                  Refresh
                </Button>
              </ButtonGroup>
            </Box>
            <ResponsiveChoropleth
              data={casesMapOptions?.data}
              features={casesMapOptions?.features}
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              colors="YlOrBr"
              domain={casesMapOptions?.domain}
              unknownColor="#666666"
              projectionScale={130}
              label="properties.name"
              valueFormat=".1s"
              projectionType="equalEarth"
              projectionTranslation={[0.5, 0.5]}
              projectionRotation={[0, 0, 0]}
              enableGraticule={true}
              graticuleLineColor="#dddddd"
              borderWidth={0.5}
              borderColor="#152538"
              legends={[
                {
                  anchor: "bottom-left",
                  direction: "column",
                  justify: true,
                  translateX: 20,
                  translateY: -10,
                  itemsSpacing: 0,
                  itemWidth: 94,
                  itemHeight: 18,
                  itemDirection: "left-to-right",
                  itemTextColor: "#444444",
                  itemOpacity: 0.85,
                  symbolSize: 18,
                  effects: [
                    {
                      on: "hover",
                      style: {
                        itemTextColor: "#000000",
                        itemOpacity: 1,
                      },
                    },
                  ],
                },
              ]}
            />
          </Box>
        </HStack>
      )}
    </Stack>
  );
};
